import {Alert, Box, Button, CircularProgress, Typography} from "@mui/material";
import {useFlag} from "@variocube/app-ui";
import {PaymentFlow} from "@variocube/payment-js";
import React, {useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useBooking} from "../../context/booking";
import {BookingEventType, useBookingClient, useBookingEvent} from "../../data/bookings";
import {B2Booking} from "../../data/client.gen";
import {useLocalization} from "../../i18n";
import {getEnvironment} from "../../utils";

let timeout: ReturnType<typeof setTimeout>;

export function BookingPayment() {
	const navigate = useNavigate();
	const {booking, refresh} = useBooking();

	useEffect(() => {
		if (!!booking && !booking.paymentId) {
			navigate(`/${booking?.tenant.id}/bookings/${booking?.id}`, {replace: true});
		}
	}, [booking]);

	if (booking) {
		const client = useBookingClient(booking.id);
		useBookingEvent(client, BookingEventType.B2ConfirmationEvent, event => {
			console.info("Received booking confirmation event", event);
			if (event.paymentId === null || !!event.paidAt) {
				console.info("Booking is paid. Redirect to booking page.");
				refresh()
					.catch(err => console.error("Failed to refresh booking after confirmation event", err))
					.finally(() => location.href = `/${booking.tenant.id}/bookings/${booking.id}`);
			}
		});
	}

	function handleSuccess() {
		timeout = setTimeout(async () => {
			try {
				const fetched = await refresh();
				if (!fetched?.paymentId || fetched?.paymentStatus === "paid") {
					console.info("Booking payment status updated to PAID. Redirect to booking page.");
					navigate(`/${fetched?.tenant.id}/bookings/${fetched?.id}`);
				}
			}
			catch (err) {
				console.error("failed to fetch booking", err);
			}
		}, 3000);
	}

	return (
		<>
			{booking && (
				<PaymentStatusAwaiter
					booking={booking}
					onSuccess={handleSuccess}
				/>
			)}
		</>
	);
}

function PaymentStatusAwaiter({booking, onSuccess}: { booking: B2Booking; onSuccess: () => void }) {
	const navigate = useNavigate();
	const {t} = useLocalization();
	const [success, setSuccess, clearSuccess] = useFlag(false);
	const [error, setError] = useState<Error>();

	function handleSuccess() {
		setSuccess();

		onSuccess();
	}

	function handleError(error: Error) {
		console.error("An error occurred when processing your payment", error);
		clearSuccess();
		setError(error);
	}

	function handleClose() {
		console.info("handle payment close");
		navigate(`/${booking?.tenant.id}/bookings/${booking?.id}`);
	}

	return (
		<>
			{error && (
				<Alert severity="error">
					{error.message ?? "An error occurred while processing your payment."}
				</Alert>
			)}
			{success && (
				<Box
					p={2}
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<CircularProgress />
					<Box my={1} />
					<Typography gutterBottom>{t("payment.successMessage")}</Typography>
					<Box my={1} />
					<Button
						variant="contained"
						color="primary"
						onClick={() => navigate(`/${booking.tenant.id}/bookings/${booking.id}`)}
					>
						{t("payment.backToBooking")}
					</Button>
				</Box>
			)}
			{!!booking && !!booking.paymentId && !success && (
				<PaymentFlow
					paymentId={booking.paymentId}
					onSucceeded={handleSuccess}
					onClose={handleClose}
					onError={handleError}
					live={getEnvironment() === "production"}
					smallDevice={window.innerWidth <= 480}
					language={booking.locale || undefined}
				/>
			)}
			<Box my={1} flexGrow={1} />
		</>
	);
}
